/*  React  */
import { Box, Card, Flex } from '@auviknetworks/iceberg-ui';
import { Skeleton } from '@mui/material';
import React from 'react';

import {
    DashboardContextProvider,
    useDashboardContext
} from '../DashboardContext';
import {
    type Dashboard as DashboardType,
    type GroupPanel as GroupPanelType,
    LayoutItem
} from '../types';
import { Panel } from './Panel';

// Generic skeleton for a panel
// TODO: Move to a separate file
const InnerPanelSkeleton = ({
    width,
    height
}: {
    width: string;
    height: string;
}) => (
    <Card
        flexDirection="column"
        padding="16px"
        width={width}
        height={height}
        minWidth="320px"
    >
        <Flex flexDirection="row" justifyContent="space-between">
            <Skeleton
                variant="text"
                width="60%"
                height={28}
                sx={{ marginBottom: '14px' }}
            />
            <Flex flexDirection="row" sx={{ gap: '4px' }} margin="4px 0">
                <Skeleton
                    variant="circular"
                    width={16}
                    height={16}
                    sx={{ borderRadius: '50%' }}
                />
                <Skeleton
                    variant="circular"
                    width={16}
                    height={16}
                    sx={{ borderRadius: '50%' }}
                />
            </Flex>
        </Flex>
        <Skeleton variant="rounded" width="100%" height="60%" />
    </Card>
);

export const GroupPanelSkeleton = () => (
    <Flex
        flexDirection="column"
        padding="24px"
        width="100%"
        height="709px"
        paddingBottom="64px"
    >
        {/* Group panel title */}
        <Skeleton
            variant="rounded"
            width="30%"
            height={32}
            sx={{ marginBottom: '24px' }}
        />
        {/* First row: two panels, 1/3 and 2/3 width */}
        <Flex
            marginBottom="24px"
            width="100%"
            height="50%"
            sx={{ gap: '24px' }}
        >
            <InnerPanelSkeleton width="33.33%" height="100%" />
            <InnerPanelSkeleton width="66.66%" height="100%" />
        </Flex>
        {/* Second row: one panel, full width */}
        <Flex width="100%" height="50%">
            <InnerPanelSkeleton width="100%" height="100%" />
        </Flex>
    </Flex>
);

interface GroupPanelProps {
    /** The panel to display. */
    panel: GroupPanelType;
    /** The workspace to display. */
    Workspace: React.ComponentType<{ schema: DashboardType | GroupPanelType }>;
}

/**
 * GroupPanel is a panel that contains other panels.
 *
 * It shares behaviours like filtring and searching with the Dashboard. It wrapps the internal state with a custom
 * Dashboard Context so the panels below act like they would be part of a regular Dashboard.
 *
 * We pass the Workspace component as a prop to prevent circular dependencies.
 */
export const GroupPanel = ({ panel, Workspace }: GroupPanelProps) => {
    const {
        dashboardState: { isEditing },
        schema: dashboard,
        setSchema
    } = useDashboardContext();

    // If the group content is updated, we need to update the layout parent dashboard definition.
    const handleLayoutChange = (layout: LayoutItem[]) => {
        if (isEditing) {
            setSchema({
                ...dashboard,
                panels: dashboard.panels.map((p) => {
                    if (p.id === panel.id) {
                        return { ...p, layout };
                    }
                    return p;
                })
            });
        }
    };

    // Like above, we pass the schema handler to the Dashboard Context so if the group content is updated,
    // we need to update the layout parent dashboard definition.
    // TODO: We should handle all schema updates with one function.
    const handleSchemaChange = (schema: GroupPanelType | DashboardType) => {
        setSchema({
            ...dashboard,
            panels: dashboard.panels.map((p) => {
                if (p.id === panel.id) {
                    return schema as GroupPanelType;
                }
                return p;
            })
        });
    };

    return (
        <Panel
            panel={{
                ...panel,
                format: { titleSize: 'large', ...panel.format }
            }}
            skeleton={<GroupPanelSkeleton />}
        >
            <DashboardContextProvider
                dashboard={{ ...dashboard, meta: panel.meta } as DashboardType}
                handleLayoutChange={handleLayoutChange}
                isEditing={isEditing}
                setSchema={handleSchemaChange}
            >
                {/* We set tags to undefined to avoid passing panel's tags. */}
                <Box padding="0 24px">
                    <Workspace
                        schema={{ ...panel, tags: undefined } as GroupPanelType}
                    />
                </Box>
            </DashboardContextProvider>
        </Panel>
    );
};
